import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../lib/api";
import Navbar from "../components/Navbar";
import { useLang } from "../contexts/LanguageContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { CheckCircle2, XCircle, RotateCw, MessageSquare, ArrowLeft } from "lucide-react";
import ReferenceLink from "@/components/ReferenceLink";

export default function Results() {
  const { attemptId } = useParams();
  const navigate = useNavigate();
  const { t } = useLang();
  const [data, setData] = useState(null);

  useEffect(() => {
    // NVDB attempts are graded client-side and stashed in sessionStorage.
    if (attemptId?.startsWith("nvdb_")) {
      const stash = sessionStorage.getItem(attemptId);
      if (stash) {
        try { setData(JSON.parse(stash)); return; } catch (e) { /* fall through */ }
      }
    }
    api.get(`/quiz/attempt/${attemptId}`).then(r => setData(r.data));
  }, [attemptId]);

  if (!data) return (
    <div className="min-h-screen bg-gray-50"><Navbar/><div className="p-10 text-center">{t("loading")}</div></div>
  );

  const passColor = data.passed ? "bg-emerald-600" : "bg-red-600";
  const passText = data.passed ? "text-emerald-700" : "text-red-700";
  const passBg = data.passed ? "bg-emerald-50" : "bg-red-50";
  const passDisplay = data.pass_threshold || (data.mode === "mock" ? "38/45" : "70%");

  const askTutor = (r) => {
    navigate("/tutor", {
      state: {
        initialPrompt: t("explainThis"),
        questionContext: {
          question: r.question, options: r.options,
          correct_index: r.correct_index, user_answer: r.user_answer,
        }
      }
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar/>
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <button data-testid="results-back" onClick={()=>navigate("/dashboard")} className="text-sm text-fjord hover:underline mb-4 inline-flex items-center gap-1">
          <ArrowLeft className="w-4 h-4 rtl:rotate-180"/> {t("backToDashboard")}
        </button>

        {/* Score card */}
        <Card className={`p-8 border-2 ${data.passed?"border-emerald-200":"border-red-200"} ${passBg} mb-8`}>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
            <div>
              <div className="text-xs uppercase tracking-widest text-gray-500 mb-2">{data.mode === "mock" ? t("mockExam") : t("practice")}</div>
              <div data-testid="results-score" className={`font-heading text-5xl font-bold ${passText} mb-2`}>
                {data.correct}/{data.total} <span className="text-2xl opacity-70">· {data.accuracy}%</span>
              </div>
              <Badge className={`${passColor} text-white`}>
                {data.passed ? t("passed") : t("failed")} · {t("passMark")} {passDisplay}
              </Badge>
            </div>
            <div className="grid grid-cols-3 gap-4 text-center">
              <Stat label={t("correct")} val={`${data.correct}/${data.total}`}/>
              <Stat label={t("accuracy")} val={`${data.accuracy}%`}/>
              <Stat label={t("duration")} val={fmtDuration(data.duration_seconds)}/>
            </div>
          </div>
          <div className="mt-6 flex flex-wrap gap-2">
            <Button data-testid="results-try-again" variant="outline" onClick={()=>navigate(data.mode==="mock"?"/mock":`/practice${data.topic?`?topic=${data.topic}`:""}`)}>
              <RotateCw className="w-4 h-4 me-2"/> {t("tryAgain")}
            </Button>
            <Button data-testid="results-go-tutor" onClick={()=>navigate("/tutor")} className="bg-fjord hover:bg-fjord-700">
              <MessageSquare className="w-4 h-4 me-2"/> {t("aiTutor")}
            </Button>
          </div>
        </Card>

        {/* Review */}
        <h2 className="font-heading text-xl font-semibold mb-4">{t("reviewAnswers")}</h2>
        <div className="space-y-4">
          {data.results.map((r, i) => {
            const correctOpt = r.options[r.correct_index];
            const userOpt = r.user_answer !== null && r.user_answer !== undefined ? r.options[r.user_answer] : null;
            return (
              <Card key={`${r.question_id}-${i}`} data-testid={`review-${r.question_id}-${i}`} className="p-5 bg-white border border-gray-200">
                <div className="flex items-start gap-3">
                  <div className={`w-8 h-8 rounded-md grid place-items-center shrink-0 ${r.is_correct?"bg-emerald-100 text-emerald-700":"bg-red-100 text-red-700"}`}>
                    {r.is_correct ? <CheckCircle2 className="w-4 h-4"/> : <XCircle className="w-4 h-4"/>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-gray-500 mb-1">Q{i+1} · {r.topic}</div>
                    <div className="font-medium text-gray-900 mb-3">{r.question}</div>
                    {r.image && (
                      <div className="mb-3">
                        <div className={`inline-block bg-gray-50 rounded-md p-2 border border-gray-200 ${
                          r.visual_type === "road_marking" || r.visual_type === "action_scene"
                            ? "w-48 h-32"
                            : r.visual_type === "traffic_light"
                            ? "w-16 h-36"
                            : "w-24 h-24"
                        }`}>
                          <img src={r.image} alt="Visual recap" className="w-full h-full object-contain"/>
                        </div>
                      </div>
                    )}
                    <div className="space-y-1 text-sm mb-3">
                      <div className="text-gray-500">
                        <span className="font-semibold text-gray-700">{t("yourAnswer")}:</span> {userOpt ?? "—"}
                      </div>
                      <div className="text-emerald-700">
                        <span className="font-semibold">{t("correctAnswer")}:</span> {correctOpt}
                      </div>
                    </div>
                    {r.explanation && (
                      <div className="bg-gray-50 border border-gray-200 rounded-md p-3 text-sm text-gray-700 mb-3">
                        <span className="font-semibold text-gray-900">{t("explanation")}: </span>{r.explanation}
                      </div>
                    )}
                    {r.reference && (
                      <div data-testid={`reference-${r.question_id}-${i}`} className="mb-3">
                        <ReferenceLink reference={r.reference} className="border-0 pt-0 mt-0" />
                      </div>
                    )}
                    {r.driver_action && (
                      <div
                        data-testid={`driver-action-${r.question_id}-${i}`}
                        className="bg-emerald-50 border border-emerald-200 rounded-md p-3 text-sm text-emerald-900 mb-3"
                      >
                        <div className="font-semibold text-xs uppercase tracking-wide mb-0.5">
                          {t("driverActionLabel")}
                        </div>
                        {r.driver_action}
                      </div>
                    )}
                    {r.common_mistake && (
                      <div
                        data-testid={`common-mistake-${r.question_id}-${i}`}
                        className="bg-amber-50 border border-amber-200 rounded-md p-3 text-sm text-amber-900 mb-3"
                      >
                        <div className="font-semibold text-xs uppercase tracking-wide mb-0.5">
                          {t("commonMistakeLabel")}
                        </div>
                        {r.common_mistake}
                      </div>
                    )}
                    {r.risk_explanation && (
                      <div
                        data-testid={`risk-${r.question_id}-${i}`}
                        className="bg-rose-50 border border-rose-200 rounded-md p-3 text-sm text-rose-900 mb-3"
                      >
                        <div className="font-semibold text-xs uppercase tracking-wide mb-0.5">
                          {t("riskWhyMattersLabel")}
                        </div>
                        {r.risk_explanation}
                      </div>
                    )}
                    <Button data-testid={`explain-${r.question_id}-${i}`} size="sm" variant="ghost" onClick={()=>askTutor(r)} className="text-fjord">
                      <MessageSquare className="w-3.5 h-3.5 me-1.5"/> {t("explainThis")}
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      </main>
    </div>
  );
}

function Stat({label, val}) {
  return (
    <div className="bg-white rounded-md border border-gray-200 px-4 py-3 min-w-[90px]">
      <div className="text-xs text-gray-500">{label}</div>
      <div className="font-heading font-bold text-lg text-gray-900">{val}</div>
    </div>
  );
}

function fmtDuration(seconds) {
  if (seconds == null) return "—";
  const m = Math.floor(seconds/60), s = seconds%60;
  return `${m}m ${s}s`;
}
